import React, { useEffect, useState } from 'react';
import { MobileLine } from '../types/MobileLine';
import api from '../services/api';
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { showError } from '../utils/toastUtils';
import Navbar from '../components/Navbar';

const Dashboard: React.FC = () => {
  const [lines, setLines] = useState<MobileLine[]>([]);
  const [loading, setLoading] = useState(false);

  // Dados de gráficos
  const [statusData, setStatusData] = useState<any[]>([]);
  const [valorData, setValorData] = useState<any[]>([]);
  const [gigasData, setGigasData] = useState<any[]>([]);

  // Cores
  const colors = {
    adimplente: '#10b981',      // Esmeralda (Adimplentes)
    inadimplente: '#f97316',    // Laranja (Inadimplentes)
    semUsuario: '#6b7280',      // Cinza (Sem Usuário)
    bloqueado: '#ef4444',
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const allLines = await api.getAllLines();
      setLines(allLines);

      // Processar dados para gráficos
      processChartData(allLines);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      showError('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processChartData = (allLines: MobileLine[]) => {
    // 1. Status Breakdown (Pie Chart)
    const statusBreakdown = {
      adimplente: 0,
      inadimplente: 0,
      semUsuario: 0,
    };

    const gigasByStatus: { [key: string]: number } = {
      adimplente: 0,
      inadimplente: 0,
      semUsuario: 0,
    };
    const valorByStatus: { [key: string]: number } = {
      adimplente: 0,
      inadimplente: 0,
      semUsuario: 0,
    };

    allLines.forEach((line) => {
      const status = line.status || 'semUsuario';
      statusBreakdown[status as keyof typeof statusBreakdown]++;

      // Gigas e valor por status
      const gigas = line.gigasDisponiveis || 0;
      const valor = line.valorMensal || 0;

      gigasByStatus[status] = (gigasByStatus[status] || 0) + gigas;
      valorByStatus[status] = (valorByStatus[status] || 0) + valor;
    });

    // Formatar para Pie Chart
    const pieData = Object.entries(statusBreakdown).map(([status, count]) => ({
      name: status === 'semUsuario' ? 'Sem Usuário' : status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      status,
    }));

    setStatusData(pieData);

    // Formatar para Bar Charts
    const valorChartData = [
      {
        name: 'Adimplentes',
        value: valorByStatus.adimplente,
        fill: colors.adimplente,
      },
      {
        name: 'Inadimplentes',
        value: valorByStatus.inadimplente,
        fill: colors.inadimplente,
      },
      {
        name: 'Sem Usuário',
        value: valorByStatus.semUsuario,
        fill: colors.semUsuario,
      },
    ];

    setValorData(valorChartData);

    const gigasChartData = [
      {
        name: 'Adimplentes',
        value: gigasByStatus.adimplente,
        fill: colors.adimplente,
      },
      {
        name: 'Inadimplentes',
        value: gigasByStatus.inadimplente,
        fill: colors.inadimplente,
      },
      {
        name: 'Sem Usuário',
        value: gigasByStatus.semUsuario,
        fill: colors.semUsuario,
      },
    ];

    setGigasData(gigasChartData);
  };

  // Cálculos gerais
  const totalLinhas = lines.length;
  const adimplentes = lines.filter((l) => l.status === 'adimplente').length;
  const inadimplentes = lines.filter((l) => l.status === 'inadimplente').length;
  const totalValor = lines.reduce((sum, l) => sum + (l.valorMensal || 0), 0);
  const totalGigas = lines.reduce((sum, l) => sum + (l.gigasDisponiveis || 0), 0);

  // Cards de estatísticas com nova paleta
  const StatCard = ({
    title,
    value,
    subtitle,
    bgGradient,
    icon,
  }: {
    title: string;
    value: string | number;
    subtitle?: string;
    bgGradient: string;
    icon?: string;
  }) => (
    <div className={`${bgGradient} rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide opacity-90">{title}</h3>
          <p className="text-4xl font-bold text-white mt-2">{value}</p>
          {subtitle && <p className="text-xs mt-2 opacity-80">{subtitle}</p>}
        </div>
        {icon && <div className="text-4xl opacity-20">{icon}</div>}
      </div>
    </div>
  );

  const COLORS_PIE = ['#10b981', '#f97316', '#6b7280'];

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 text-white">
      {/* Navbar */}
      <Navbar currentPage="dashboard" />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-6">
        <StatCard 
          title="Total de Linhas" 
          value={totalLinhas} 
          bgGradient="bg-gradient-to-br from-blue-600 to-blue-700"
          icon="📱"
        />
        <StatCard
          title="✓ Adimplentes"
          value={adimplentes}
          subtitle={`${((adimplentes / totalLinhas) * 100).toFixed(1)}%`}
          bgGradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
          icon="✅"
        />
        <StatCard
          title="⚠️ Inadimplentes"
          value={inadimplentes}
          subtitle={`${((inadimplentes / totalLinhas) * 100).toFixed(1)}%`}
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
          icon="⚠️"
        />
        <StatCard
          title="💰 Valor Total"
          value={`R$ ${totalValor.toFixed(2)}`}
          subtitle="Mensal"
          bgGradient="bg-gradient-to-br from-green-600 to-green-700"
          icon="💵"
        />
        <StatCard 
          title="📊 Total Gigas" 
          value={totalGigas.toFixed(0)} 
          bgGradient="bg-gradient-to-br from-purple-600 to-purple-700"
          icon="📡"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
        {/* Status Distribution - Pie Chart */}
        <div className="bg-zinc-800 rounded-lg p-6 shadow-lg">
          <h2 className="text-xl font-bold text-blue-400 mb-4">📍 Distribuição por Status</h2>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, value, percent }) =>
                    `${name}: ${value} (${percent ? (percent * 100).toFixed(0) : '0'}%)`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS_PIE[index % COLORS_PIE.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value} linhas`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 text-center py-8">Sem dados</p>
          )}
        </div>

        {/* Valor por Status - Bar Chart */}
        <div className="bg-zinc-800 rounded-lg p-6 shadow-lg">
          <h2 className="text-xl font-bold text-green-400 mb-4">💰 Valor Mensal por Status</h2>
          {valorData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={valorData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis dataKey="name" stroke="#999" />
                <YAxis stroke="#999" />
                <Tooltip
                  formatter={(value: any) => `R$ ${Number(value).toFixed(2)}`}
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #444' }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {valorData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 text-center py-8">Sem dados</p>
          )}
        </div>

        {/* Gigas por Status - Bar Chart */}
        <div className="bg-zinc-800 rounded-lg p-6 shadow-lg">
          <h2 className="text-xl font-bold text-purple-400 mb-4">🌐 Gigas Disponíveis por Status</h2>
          {gigasData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={gigasData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis dataKey="name" stroke="#999" />
                <YAxis stroke="#999" />
                <Tooltip
                  formatter={(value: any) => `${Number(value).toFixed(0)} GB`}
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #444' }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {gigasData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 text-center py-8">Sem dados</p>
          )}
        </div>

        {/* Summary Stats */}
        <div className="bg-zinc-800 rounded-lg p-6 shadow-lg">
          <h2 className="text-xl font-bold text-yellow-400 mb-4">📊 Resumo Geral</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-zinc-700">
              <span className="text-gray-400">Total de Linhas:</span>
              <span className="text-white font-bold">{totalLinhas}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-zinc-700">
              <span className="text-gray-400">Adimplentes:</span>
              <span className="text-green-400 font-bold">
                {adimplentes} ({((adimplentes / totalLinhas) * 100).toFixed(1)}%)
              </span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-zinc-700">
              <span className="text-gray-400">Inadimplentes:</span>
              <span className="text-yellow-400 font-bold">
                {inadimplentes} ({((inadimplentes / totalLinhas) * 100).toFixed(1)}%)
              </span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-zinc-700">
              <span className="text-gray-400">Sem Usuário:</span>
              <span className="text-gray-400 font-bold">
                {totalLinhas - adimplentes - inadimplentes}
              </span>
            </div>
            <div className="flex justify-between items-center pt-4">
              <span className="text-gray-400">Valor Total Mensal:</span>
              <span className="text-purple-400 font-bold text-lg">R$ {totalValor.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total de Gigas:</span>
              <span className="text-pink-400 font-bold text-lg">{totalGigas.toFixed(0)} GB</span>
            </div>
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="p-6">
        <button
          onClick={loadData}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg disabled:bg-gray-600"
        >
          🔄 Atualizar Dashboard
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
